import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  applyEdgeChanges,
  applyNodeChanges,
  MarkerType,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";

import { useB2bUsersStage } from "@/application/use-cases/architecture/use-b2b-users-stage.queries";
import { type WorkspacePosition } from "@/infrastructure/repositories/architecture-workspace.mappers";
import {
  type B2bUsersEdge,
  type B2bUsersEdgeKind,
  type B2bUsersNode,
} from "@/presentation/data/b2b-users-stage-seed";
import type { MapNodeData } from "@/presentation/hooks/use-architecture-map-page";

function edgePaint(kind: B2bUsersEdgeKind): { stroke: string; style: CSSProperties } {
  switch (kind) {
    case "finance":
      return {
        stroke: "oklch(0.48 0.12 72)",
        style: { stroke: "oklch(0.55 0.13 75 / 0.86)", strokeWidth: 2.4 },
      };
    case "feedback":
      return {
        stroke: "oklch(0.46 0.17 300)",
        style: {
          stroke: "oklch(0.52 0.18 300 / 0.86)",
          strokeWidth: 2.2,
          strokeDasharray: "8 6",
        },
      };
    case "manual":
      return {
        stroke: "oklch(0.4 0.1 145)",
        style: {
          stroke: "oklch(0.48 0.11 145 / 0.86)",
          strokeWidth: 2,
          strokeDasharray: "6 6",
        },
      };
    case "bidirectional":
      return {
        stroke: "oklch(0.44 0.13 200)",
        style: {
          stroke: "oklch(0.5 0.15 200 / 0.86)",
          strokeWidth: 2.2,
          strokeDasharray: "3 4",
        },
      };
    case "dependency":
      return {
        stroke: "oklch(0.4 0.02 250)",
        style: {
          stroke: "oklch(0.45 0.03 250 / 0.6)",
          strokeWidth: 1.25,
          strokeDasharray: "2 8",
        },
      };
    default:
      return {
        stroke: "oklch(0.4 0.16 195)",
        style: { stroke: "oklch(0.52 0.16 190 / 0.86)", strokeWidth: 2.2 },
      };
  }
}

function makeXyEdge(e: B2bUsersEdge): Edge {
  const paint = edgePaint(e.kind);
  const marker = { type: MarkerType.ArrowClosed, width: 24, height: 24, color: paint.stroke };
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    type: "default",
    label: e.label ?? undefined,
    style: paint.style,
    markerEnd: marker,
    markerStart: e.kind === "bidirectional" ? marker : undefined,
    labelStyle: {
      fontSize: 11,
      fontFamily: "var(--font-mono)",
      fontWeight: 700,
      fill: "oklch(0.3 0.14 200)",
    },
    labelBgStyle: { fill: "oklch(1 0 0)", fillOpacity: 0.96 },
    labelBgPadding: [8, 4] as [number, number],
    labelBgBorderRadius: 5,
  };
}

function makeXyNode(
  n: B2bUsersNode,
  positions: Record<string, WorkspacePosition>,
  onRename: (id: string, next: string) => void,
  onNoop: () => void
): Node<MapNodeData> {
  const pos = positions[n.id] ?? { x: n.x, y: n.y };
  const data: MapNodeData = {
    id: n.id,
    name: n.label,
    role: n.role,
    kind: n.kind,
    ownership: "external",
    domain: n.domain,
    stages: ["b2b-users"],
    meetings: [],
    position: pos,
    active: true,
    onToggleHighlight: onNoop,
    onRename,
  };
  return {
    id: n.id,
    type: "mapComponent",
    position: pos,
    zIndex: 10,
    data,
    draggable: true,
    selectable: true,
  };
}

export interface UseB2bUsersMapPage {
  isLoading: boolean;
  nodes: Node<MapNodeData>[];
  edges: Edge[];
  nodeCount: number;
  edgeCount: number;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (input: {
    label: string;
    role: string;
    domain: B2bUsersNode["domain"];
    kind: B2bUsersNode["kind"];
  }) => void;
  resetToSeed: () => void;
}

export function useB2bUsersMapPage(): UseB2bUsersMapPage {
  // Destructure so dependent callbacks track stable mutation refs (these are
  // useCallback'd inside useB2bUsersStage and only change when patch changes).
  const {
    isLoading,
    nodes: dataNodes,
    edges: dataEdges,
    positions: dataPositions,
    setNodes: persistNodes,
    setEdges: persistEdges,
    setPositions: persistPositions,
    resetToSeed,
  } = useB2bUsersStage();

  const dataNodesRef = useRef(dataNodes);
  const dataEdgesRef = useRef(dataEdges);
  const positionsRef = useRef(dataPositions);

  useEffect(() => {
    dataNodesRef.current = dataNodes;
  }, [dataNodes]);
  useEffect(() => {
    dataEdgesRef.current = dataEdges;
  }, [dataEdges]);
  useEffect(() => {
    positionsRef.current = dataPositions;
  }, [dataPositions]);

  const noopRef = useRef(() => {});
  const renameNodeRef = useRef<(id: string, next: string) => void>(() => {});

  const renameNode = useCallback(
    (id: string, nextLabel: string) => {
      const updated = dataNodesRef.current.map((n) =>
        n.id === id ? { ...n, label: nextLabel } : n
      );
      dataNodesRef.current = updated;
      persistNodes(updated);
    },
    [persistNodes]
  );

  // Stash the latest renameNode in a ref so we can pass a stable identity to
  // node data without invalidating the hydration effect on every render.
  useEffect(() => {
    renameNodeRef.current = renameNode;
  }, [renameNode]);

  const stableRename = useCallback((id: string, next: string) => {
    renameNodeRef.current(id, next);
  }, []);
  const stableNoop = useCallback(() => {
    noopRef.current();
  }, []);

  /** Build initial xyflow nodes/edges; the effect below syncs from workspace. */
  const [xyNodes, setXyNodes] = useState<Node<MapNodeData>[]>([]);
  const [xyEdges, setXyEdges] = useState<Edge[]>([]);
  const hydratedRef = useRef(false);

  // Hydrate once when workspace data first arrives, and re-hydrate whenever
  // the workspace data identity changes (Reset, remote update, etc.).
  // Intentional sync from TanStack-Query-backed workspace into local xyflow
  // state. Stable callback refs (stableRename, stableNoop) keep this effect's
  // dependencies pinned so it doesn't loop.
  /* eslint-disable react-hooks/set-state-in-effect -- controlled xyflow bridge from query data */
  useEffect(() => {
    if (isLoading) return;
    setXyNodes(dataNodes.map((n) => makeXyNode(n, dataPositions, stableRename, stableNoop)));
    setXyEdges(dataEdges.map(makeXyEdge));
    hydratedRef.current = true;
  }, [isLoading, dataNodes, dataEdges, dataPositions, stableRename, stableNoop]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setXyNodes((current) => applyNodeChanges(changes, current) as Node<MapNodeData>[]);
      let positionDirty = false;
      const removeIds: string[] = [];
      for (const ch of changes) {
        if (ch.type === "position" && ch.position && ch.dragging === false) {
          positionsRef.current = { ...positionsRef.current, [ch.id]: ch.position };
          positionDirty = true;
        }
        if (ch.type === "remove") {
          removeIds.push(ch.id);
        }
      }
      if (positionDirty) persistPositions(positionsRef.current);
      if (removeIds.length > 0) {
        const idSet = new Set(removeIds);
        const nextNodes = dataNodesRef.current.filter((n) => !idSet.has(n.id));
        const nextEdges = dataEdgesRef.current.filter(
          (e) => !idSet.has(e.source) && !idSet.has(e.target)
        );
        dataNodesRef.current = nextNodes;
        dataEdgesRef.current = nextEdges;
        persistNodes(nextNodes);
        persistEdges(nextEdges);
      }
    },
    [persistNodes, persistEdges, persistPositions]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setXyEdges((current) => applyEdgeChanges(changes, current));
      const removeIds = changes.filter((c) => c.type === "remove").map((c) => c.id);
      if (removeIds.length > 0) {
        const idSet = new Set(removeIds);
        const nextEdges = dataEdgesRef.current.filter((e) => !idSet.has(e.id));
        dataEdgesRef.current = nextEdges;
        persistEdges(nextEdges);
      }
    },
    [persistEdges]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (connection.source === connection.target) return;
      const edgeId = `e-${connection.source}-${connection.target}-${Date.now().toString(36)}`;
      const next: B2bUsersEdge = {
        id: edgeId,
        source: connection.source,
        target: connection.target,
        kind: "primary",
      };
      const nextEdges = [...dataEdgesRef.current, next];
      dataEdgesRef.current = nextEdges;
      persistEdges(nextEdges);
    },
    [persistEdges]
  );

  const addNode = useCallback(
    (input: {
      label: string;
      role: string;
      domain: B2bUsersNode["domain"];
      kind: B2bUsersNode["kind"];
    }) => {
      const id = `${input.kind === "actor" ? "actor" : "node"}-${Date.now().toString(36)}`;
      // Drop the node at a sensible position offset from existing nodes.
      const baseX = 600;
      const baseY = 200;
      const occupiedYs = dataNodesRef.current
        .filter((n) => Math.abs(n.x - baseX) < 60)
        .map((n) => n.y);
      let y = baseY;
      while (occupiedYs.some((oy) => Math.abs(oy - y) < 120)) y += 140;
      const next: B2bUsersNode = {
        id,
        label: input.label,
        role: input.role,
        domain: input.domain,
        kind: input.kind,
        x: baseX,
        y,
      };
      const nextNodes = [...dataNodesRef.current, next];
      dataNodesRef.current = nextNodes;
      persistNodes(nextNodes);
    },
    [persistNodes]
  );

  const nodeCount = dataNodes.length;
  const edgeCount = dataEdges.length;

  return useMemo(
    () => ({
      isLoading,
      nodes: xyNodes,
      edges: xyEdges,
      nodeCount,
      edgeCount,
      onNodesChange,
      onEdgesChange,
      onConnect,
      addNode,
      resetToSeed,
    }),
    [
      isLoading,
      resetToSeed,
      xyNodes,
      xyEdges,
      nodeCount,
      edgeCount,
      onNodesChange,
      onEdgesChange,
      onConnect,
      addNode,
    ]
  );
}
